/**
 * Message types matching the API format.
 * Supports both full messages and individual message parts for component rendering.
 */

// Usage log structure
export interface UsageLog {
    input_tokens: number;
    output_tokens: number;
    reasoning_tokens: number;
    cached_input_tokens: number;
    cached_output_tokens: number;
}

// Message metadata
export interface MessageMetadata {
    images?: Array<{ url: string }>;
    usage_log?: UsageLog;
    [key: string]: unknown; // Allows for additional metadata, althought we don't use it yet
}

// Message part types - these can be rendered by separate components
export type MessagePartType = 'thinking' | 'answer' | 'tool_use' | 'tool_result';

export const VALID_ASSISTANT_PART_TYPES: ReadonlySet<string> = new Set<string>(['thinking', 'answer', 'tool_use', 'tool_result']);

export type ToolType = 'web_search' | 'web_fetch' | 'custom' | 'sub_agent';

// Base interface for all message parts
export interface BaseMessagePart {
    type: MessagePartType;
    metadata?: Record<string, unknown> | null;
}

// Thinking message part
export interface ThinkingPart extends BaseMessagePart {
    type: MessagePartType;
    content: string;
    duration_ms?: number;
}

// Answer message part
export interface AnswerPart extends BaseMessagePart {
    type: MessagePartType;
    content: string;
}

// Tool use message part
export interface ToolUsePart extends BaseMessagePart {
    type: MessagePartType;
    tool_use_id: string;
    name: string;
    tool_type: ToolType;
    parameters?: Record<string, unknown> | null;
    metadata?: Record<string, unknown> | null;
}

// Tool result message part
export interface ToolResultPart extends BaseMessagePart {
    type: MessagePartType;
    tool_use_id: string;
    name: string;
    result?: string | null;
    metadata?: Record<string, unknown> | null;
}

export const Role = {
  user: 'user' as const,
  assistant: 'assistant' as const,
  system: 'system' as const,
} as const;

export type Role = typeof Role[keyof typeof Role];

export type MessagePart = ThinkingPart | AnswerPart | ToolUsePart | ToolResultPart;

// Asssistant message type
export interface AssistantMessage {
    id: string;
    conversation_id: string;
    role: Role;
    content: null;
    created_at: string;
    message_parts: MessagePart[];
    metadata: MessageMetadata;
}

// User message type
export interface UserMessage {
    id: string;
    conversation_id: string;
    role: Role;
    content: string;
    created_at: string;
    message_parts: null;
    metadata: MessageMetadata;
}

export type Message = AssistantMessage | UserMessage;